// ABOUTME: Modal for editing branded login page settings (color, logo, side image).
// ABOUTME: Calls PUT /api/brand via useUpdateBrandMutation to persist changes.

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import { SHPBBackgroundColor } from "../../../components/portalbuilder/Edittors/SHPBBackgroundColor";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import Shbutton from "../../theme/buttons/Shbutton";
import SHModal from "../../theme/Modal/SHModal";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import UploadLogoSelector from "../../theme/upload/logo/UploadLogoSelector";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import { useGetCustomerBrandQuery, useUpdateBrandMutation } from "../../../newapi/global/brandRootSlice";
import { hexToRgb } from "../../../utils/colorConverters";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from '../../../utils/toasts';
import { useSelector } from 'react-redux';

const MEditBrandedLogin = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [color, setColor] = useState({ hex: "#0D61FF", opacity: 1 });
    const [separateMode, setSeparateMode] = useState(false);

    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    // Single mode
    const [logoFile, setLogoFile] = useState(null);
    const [sideImageFile, setSideImageFile] = useState(null);

    // Separate mode
    const [logoLightFile, setLogoLightFile] = useState(null);
    const [logoDarkFile, setLogoDarkFile] = useState(null);
    const [sideImageLightFile, setSideImageLightFile] = useState(null);
    const [sideImageDarkFile, setSideImageDarkFile] = useState(null);

    // Preview URLs from API
    const [logoLightUrl, setLogoLightUrl] = useState(null);
    const [logoDarkUrl, setLogoDarkUrl] = useState(null);
    const [sideImageLightUrl, setSideImageLightUrl] = useState(null);
    const [sideImageDarkUrl, setSideImageDarkUrl] = useState(null);

    const [updateBrand] = useUpdateBrandMutation();

    const { data: brandData } = useGetCustomerBrandQuery(undefined, {
        skip: !props.open,
    });

    useEffect(() => {
        if (!brandData) return;
        const brand = Object.values(brandData)[0];
        if (brand?.color?.hex) {
            setColor({ hex: brand.color.hex, opacity: brand.color.opacity ?? 1 });
        }

        // Load existing image URLs
        const ll = brand?.logo?.light || null;
        const ld = brand?.logo?.dark || null;
        const sl = brand?.sideImage?.light || null;
        const sd = brand?.sideImage?.dark || null;

        setLogoLightUrl(ll);
        setLogoDarkUrl(ld);
        setSideImageLightUrl(sl);
        setSideImageDarkUrl(sd);

        // Auto-detect separate mode: if light and dark URLs differ
        const logosDiffer = ll && ld && ll !== ld;
        const sideImagesDiffer = sl && sd && sl !== sd;
        if (logosDiffer || sideImagesDiffer) {
            setSeparateMode(true);
        }
    }, [brandData]);

    const handleClose = () => {
        props.handleClose();
    };

    const handleSave = async () => {
        if (separateMode) {
            await updateBrand({
                colorHex: color.hex,
                colorOpacity: color.opacity,
                logoLight: logoLightFile,
                logoDark: logoDarkFile || logoLightFile,
                sideImageLight: sideImageLightFile,
                sideImageDark: sideImageDarkFile || sideImageLightFile,
            }).unwrap().then(res => {
                showToastMessageUpdateRequest(t,res);
                 handleClose();

            }).catch(err => {
                showToastMessageRequestError(t('create_failed'), t, err);
            });
        } else {
            await updateBrand({
                colorHex: color.hex,
                colorOpacity: color.opacity,
                logoLight: logoFile,
                logoDark: logoFile,
                sideImageLight: sideImageFile,
                sideImageDark: sideImageFile,
            }).unwrap().then(res=> {
                showToastMessageUpdateRequest(t,res);
                 handleClose();

            }).catch(err => {
                showToastMessageRequestError(t('create_failed'), t, err);

            });
        }

    };

    const brandRgb = color?.hex ? hexToRgb(color.hex) : null;

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }
    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems:'center',
                }}
            >
                <Subheading>{t(isFulfilment ? "branded_login" : "login_style")}</Subheading>
                <Text light>{t(isFulfilment ? "branded_login_description" : "login_style_description")}</Text>

                {/* Single / Separate toggle */}
                <Box
                    sx={{
                        height: 40,
                        borderRadius: 5,
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        p: 0.5,
                        mt: 2,
                    }}
                >
                    <ButtonBase
                        onClick={() => setSeparateMode(false)}
                        sx={{
                            height: 32,
                            flex: 1,
                            px: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            borderRadius: 4,
                            background: !separateMode
                                ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0])
                                : "transparent",
                            color: !separateMode ? colors.txt["primary"] : colors.txt["secondary"],
                        }}
                    >
                        {t('single')}
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => setSeparateMode(true)}
                        sx={{
                            height: 32,
                            flex: 1,
                            px: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            borderRadius: 4,
                            whiteSpace:'nowrap',
                            background: separateMode
                                ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0])
                                : "transparent",
                            color: separateMode ? colors.txt["primary"] : colors.txt["secondary"],
                        }}
                    >
                        {t('separate')}
                    </ButtonBase>
                </Box>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                {/* Main color */}
                <SHPBBackgroundColor value={color} setValue={setColor} />

                {/* Logo upload */}
                <Box>
                    <Text sx={{ pb: 1 }}>Logo</Text>
                    {separateMode ? (
                        <Box sx={{
                            display: "flex",
                            gap: 2,
                            p: 1.5,
                            borderRadius: 6,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                        }}>
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <Text light sx={{ pb: 1.5, pl: 1.5, fontSize: 12 }}>{t('light')}</Text>
                                <UploadLogoSelector
                                    title="Logo"
                                    variant="logo"
                                    brandColor={brandRgb}
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    icon={<BrandingWatermarkOutlinedIcon />}
                                    previewUrl={logoLightUrl}
                                    onFileSelect={(file) => setLogoLightFile(file)}
                                    onRemove={() => setLogoLightUrl(null)}
                                />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <Text light sx={{ pb: 1.5, fontSize: 12 }}>{t('dark')}</Text>
                                <UploadLogoSelector
                                    title="Logo"
                                    variant="logo"
                                    brandColor={brandRgb}
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    icon={<BrandingWatermarkOutlinedIcon />}
                                    previewUrl={logoDarkUrl}
                                    onFileSelect={(file) => setLogoDarkFile(file)}
                                    onRemove={() => setLogoDarkUrl(null)}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <UploadLogoSelector
                            title="Logo"
                            variant="logo"
                            brandColor={brandRgb}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            icon={<BrandingWatermarkOutlinedIcon />}
                            previewUrl={logoLightUrl}
                            onFileSelect={(file) => setLogoFile(file)}
                            onRemove={() => { setLogoLightUrl(null); setLogoDarkUrl(null); }}
                        />
                    )}
                </Box>

                {/* Side image upload */}
                <Box>
                    <Text sx={{ pb: 1 }}>{t("side_image")}</Text>
                    {separateMode ? (
                        <Box sx={{
                            display: "flex",
                            gap: 2,
                            p: 1.5,
                            borderRadius: 6,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.bg["tertiary"],
                        }}>
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <Text light sx={{ pb: 1.5, pl: 1.5, fontSize: 13 }}>{t('light')}</Text>
                                <UploadLogoSelector
                                    title={t("side_image")}
                                    variant="sideImage"
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    icon={<ImageOutlinedIcon />}
                                    previewUrl={sideImageLightUrl}
                                    onFileSelect={(file) => setSideImageLightFile(file)}
                                    onRemove={() => setSideImageLightUrl(null)}
                                />
                            </Box>
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <Text light sx={{ pb: 1.5, fontSize: 12 }}>{t('dark')}</Text>
                                <UploadLogoSelector
                                    title={t("side_image")}
                                    variant="sideImage"
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    icon={<ImageOutlinedIcon />}
                                    previewUrl={sideImageDarkUrl}
                                    onFileSelect={(file) => setSideImageDarkFile(file)}
                                    onRemove={() => setSideImageDarkUrl(null)}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <UploadLogoSelector
                            title={t("side_image")}
                            variant="sideImage"
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            icon={<ImageOutlinedIcon />}
                            previewUrl={sideImageLightUrl}
                            onFileSelect={(file) => setSideImageFile(file)}
                            onRemove={() => { setSideImageLightUrl(null); setSideImageDarkUrl(null); }}
                        />
                    )}
                </Box>
            </Box>

            {/* FOOTER */}
               <Box
                component="footer"
                sx={{
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={() => {
                        handleClose(false);
                    }}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon/>}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton className={"group-btn"} color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    onClick={handleSave}
                    >{t("save")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MEditBrandedLogin;
